import React, { useState } from "react";
import { getFirestore, collection, getDocs, query, orderBy, limit, setDoc, doc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

const firestore = getFirestore();

const GRUPOS_SANGUINEOS = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'];
const ESTADOS_SEGURO = ['Activo', 'Inactivo', 'Pendiente', 'En trámite', 'No aplica'];

const IMAGE_FIELDS = [
    { key: 'fotoCarnet', label: 'Foto Carnet' },
    { key: 'ciFrente', label: 'CI Frente' },
    { key: 'ciDorso', label: 'CI Dorso' },
    { key: 'registroFrente', label: 'Registro Frente' },
    { key: 'registroDorso', label: 'Registro Dorso' },
];

function AddFuncionario() {
    const [state, setState] = useState({
        nombre: '',
        nroCedula: '',
        correo: '',
        ciudad: '',
        grupoSanguineo: '',
        nombreContacto: '',
        seguroKemi: '',
        seguroSancor: '',
        latitud: '',
        longitud: '',
        telefono: '',
        contactoEmergencia: '',
        submitted: false,
        spinner: false,
    });
    const [images, setImages] = useState({
        fotoCarnet: null,
        ciFrente: null,
        ciDorso: null,
        registroFrente: null,
        registroDorso: null,
    });

    const onChangeValues = (e) => {
        const { name, value } = e.target;
        setState(prev => ({ ...prev, [name]: value }));
    };

    const onChangeImage = (key, file) => {
        setImages(prev => ({ ...prev, [key]: file }));
    };

    const uploadImage = async (funcionarioId, key, file) => {
        if (!file) return null;
        const rawExt = file.name && file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
        const cleanExt = rawExt.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
        const path = `funcionarios/${funcionarioId}/${key}_${Date.now()}.${cleanExt}`;
        const fileRef = storageRef(storage, path);
        const metadata = {
            contentType: file.type || 'image/jpeg',
        };
        await uploadBytes(fileRef, file, metadata);
        return await getDownloadURL(fileRef);
    };

    const saveFuncionario = async () => {
        setState(prev => ({ ...prev, spinner: true }));
        try {
            const colRef = collection(firestore, "funcionarios");
            const q = query(colRef, orderBy("id", "desc"), limit(1));
            const snapshot = await getDocs(q);

            let lastId = 0;
            if (!snapshot.empty) {
                snapshot.forEach(d => { lastId = d.data().id; });
            }
            const newId = lastId + 1;

            const imageUrls = {};
            for (const { key } of IMAGE_FIELDS) {
                imageUrls[key] = await uploadImage(newId, key, images[key]);
            }

            const data = {
                id: newId,
                nombre: state.nombre,
                nroCedula: state.nroCedula,
                correo: state.correo,
                ciudad: state.ciudad,
                grupoSanguineo: state.grupoSanguineo,
                nombreContacto: state.nombreContacto,
                seguroKemi: state.seguroKemi,
                seguroSancor: state.seguroSancor,
                latitud: state.latitud,
                longitud: state.longitud,
                telefono: state.telefono,
                contactoEmergencia: state.contactoEmergencia,
                ...imageUrls,
            };

            await setDoc(doc(firestore, "funcionarios", newId.toString()), data);
            setState(prev => ({ ...prev, submitted: true, spinner: false }));
        } catch (error) {
            console.error("Error al guardar funcionario:", error);
            setState(prev => ({ ...prev, spinner: false }));
        }
    };

    return (
        <Container component="main" maxWidth="md">
            {state.submitted ? (
                <div>
                    <Typography variant="h4">Funcionario creado correctamente!</Typography>
                    <Button href="/funcionarios">Ir al listado</Button>
                </div>
            ) : (
                <div className="form-container">
                    {state.spinner ? (
                        <CircularProgress />
                    ) : (
                        <>
                            <Typography component="h1" variant="h5">Nuevo Funcionario</Typography>
                            <div className="order-container">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            required
                                            fullWidth
                                            className="default__textfield"
                                            id="nombre"
                                            label="Nombre completo"
                                            value={state.nombre}
                                            name="nombre"
                                            onChange={onChangeValues}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            required
                                            fullWidth
                                            className="default__textfield"
                                            id="nroCedula"
                                            label="Número de cédula"
                                            value={state.nroCedula}
                                            name="nroCedula"
                                            onChange={onChangeValues}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="correo"
                                            label="Correo"
                                            type="email"
                                            value={state.correo}
                                            name="correo"
                                            onChange={onChangeValues}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="ciudad"
                                            label="Ciudad"
                                            value={state.ciudad}
                                            name="ciudad"
                                            onChange={onChangeValues}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="grupoSanguineo"
                                            label="Grupo sanguíneo"
                                            value={state.grupoSanguineo}
                                            name="grupoSanguineo"
                                            onChange={onChangeValues}
                                        >
                                            <MenuItem value="">-</MenuItem>
                                            {GRUPOS_SANGUINEOS.map(g => (
                                                <MenuItem key={g} value={g}>{g}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="telefono"
                                            label="Teléfono"
                                            value={state.telefono}
                                            name="telefono"
                                            onChange={onChangeValues}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="nombreContacto"
                                            label="Nombre del contacto"
                                            value={state.nombreContacto}
                                            name="nombreContacto"
                                            onChange={onChangeValues}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="contactoEmergencia"
                                            label="Teléfono del contacto"
                                            value={state.contactoEmergencia}
                                            name="contactoEmergencia"
                                            onChange={onChangeValues}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="seguroKemi"
                                            label="Estado seguro Kemi (médico)"
                                            value={state.seguroKemi}
                                            name="seguroKemi"
                                            onChange={onChangeValues}
                                        >
                                            <MenuItem value="">-</MenuItem>
                                            {ESTADOS_SEGURO.map(e => (
                                                <MenuItem key={e} value={e}>{e}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="seguroSancor"
                                            label="Estado seguro Sancor (accidentes)"
                                            value={state.seguroSancor}
                                            name="seguroSancor"
                                            onChange={onChangeValues}
                                        >
                                            <MenuItem value="">-</MenuItem>
                                            {ESTADOS_SEGURO.map(e => (
                                                <MenuItem key={e} value={e}>{e}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="latitud"
                                            label="Latitud (coordenadas casa)"
                                            value={state.latitud}
                                            name="latitud"
                                            onChange={onChangeValues}
                                            placeholder="-31.4201"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            className="default__textfield"
                                            id="longitud"
                                            label="Longitud (coordenadas casa)"
                                            value={state.longitud}
                                            name="longitud"
                                            onChange={onChangeValues}
                                            placeholder="-64.1888"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Documentación (imágenes)</Typography>
                                    </Grid>
                                    {IMAGE_FIELDS.map(({ key, label }) => (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                                                <Button
                                                    component="label"
                                                    variant="outlined"
                                                    startIcon={<CloudUploadIcon />}
                                                    size="small"
                                                >
                                                    {label}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        onChange={(e) => onChangeImage(key, e.target.files?.[0] || null)}
                                                    />
                                                </Button>
                                                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {images[key]?.name || 'Sin archivo'}
                                                </Typography>
                                                {images[key] && (
                                                    <IconButton size="small" onClick={() => onChangeImage(key, null)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                                <Button
                                    type="button"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    className="button__save"
                                    onClick={saveFuncionario}
                                    disabled={!state.nombre || !state.nroCedula}
                                >
                                    Guardar
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </Container>
    );
}

export default AddFuncionario;
